import { Repeat, CalendarPlus } from "lucide-react";
import DeclinedItemsList from "./DeclinedItemsList";

// AcceptedPlanSequence — the "your plan is set" view for Accepted and
// Partially Accepted plans. A green visit sequence of the accepted work,
// with a struck-through Declined strip beneath (partial case) and an
// explicit "Schedule visits" action (scheduling is no longer the default
// board). The plan total reflects only accepted items — declined ones
// are already excluded from total_price_centavos.

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}
function itemLine(it) {
  const proc = it.procedure_name || "Procedure";
  const tooth = it.tooth_number ? `#${it.tooth_number}` : "";
  const surface = it.surface ? ` ${it.surface}` : "";
  return [proc, tooth, surface].filter(Boolean).join(" ").trim();
}
function visitCountOf(it) {
  const v = Array.isArray(it?.procedure_visits) ? it.procedure_visits.length : 0;
  if (v > 1) return v;
  return Number(it?.visit_count) > 1 ? Number(it.visit_count) : 1;
}

export default function AcceptedPlanSequence({ plan, onSchedule }) {
  const items = plan?.items ?? [];
  const accepted = items.filter((it) => it.status !== "Declined" && it.status !== "Cancelled");
  const declinedItems = items.filter((it) => it.status === "Declined");
  const declinedTotal = declinedItems.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);
  const planTotal = Number.isFinite(Number(plan?.total_price_centavos))
    ? Number(plan.total_price_centavos)
    : accepted.reduce((s, it) => s + (Number(it.fee_centavos) || 0), 0);
  const hasDeclined = declinedItems.length > 0;

  return (
    <div data-testid="accepted-plan-sequence" className="space-y-3">
      <div data-testid="accepted-sequence" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-2">Visit sequence</p>
        <ol className="space-y-2">
          {accepted.map((it, idx) => {
            const vc = visitCountOf(it);
            return (
              <li key={it.id} data-testid={`accepted-seq-item-${it.id}`} className="flex items-center gap-2.5">
                <span className="w-5 h-5 rounded-full bg-white text-emerald-700 text-[11px] font-medium flex items-center justify-center shrink-0">{idx + 1}</span>
                <span className="flex-1 text-sm text-slate-900">
                  {itemLine(it)}
                  {vc > 1 ? (
                    <span className="ml-1.5 inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded align-middle">
                      <Repeat className="w-2.5 h-2.5" /> {vc} visits
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-slate-500 tabular-nums">{formatPHP(Number(it.fee_centavos) || 0)}</span>
              </li>
            );
          })}
        </ol>
      </div>

      {hasDeclined ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Declined <span className="font-normal normal-case tracking-normal">· on record, not billed</span></p>
          <DeclinedItemsList items={items} />
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <span className="text-sm text-slate-600">
          Plan total{" "}
          <span data-testid="accepted-plan-total" className="text-slate-900 font-medium tabular-nums">{formatPHP(planTotal)}</span>
          {hasDeclined ? <span className="text-slate-400"> · declined {formatPHP(declinedTotal)}</span> : null}
        </span>
        <button
          type="button"
          onClick={() => onSchedule?.()}
          data-testid="accepted-schedule"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
        >
          <CalendarPlus className="w-3.5 h-3.5" /> Schedule visits
        </button>
      </div>
    </div>
  );
}
